import { useState, useEffect, useRef } from "react";
import { handleExternalNav } from "@/lib/utils";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, Edit2, Trash2, Instagram, Facebook, Globe, Music2, User, Mail,
  MapPin, Star, Car, Utensils, Phone, Calendar, Search, Users, Building2, Loader2,
  Import, Smartphone, Check, Flag, MessageSquare, ExternalLink, Map as MapIcon, ChevronRight, ArrowLeft
} from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

// X (Twitter) Logo SVG Component
const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Check if running in Capacitor (mobile app)
const isCapacitor = typeof window !== 'undefined' && window.Capacitor !== undefined;

// Fix Leaflet default marker icon (webpack strips default icon paths)
const venueIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// ───── Alphabetical grouping ──────────────────────────────────────────
// Bucket items by their first character: A-Z get a letter bucket,
// anything else (digits, symbols — e.g. venues like "100 Club") goes in
// the "#" bucket. Returns an ordered list of [bucketKey, items] pairs,
// skipping empty buckets, with "#" rendered first if present then A-Z.
function groupByInitial(items, getName) {
  const buckets = new Map();
  items.forEach((item) => {
    const name = (getName(item) || "").trim();
    const first = name.charAt(0).toUpperCase();
    const key = /^[A-Z]$/.test(first) ? first : "#";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(item);
  });
  const order = ["#", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))];
  return order.filter((k) => buckets.has(k)).map((k) => [k, buckets.get(k)]);
}

// Renders grouped sections with a letter heading + a card grid per group.
// Pass `groups` as the output of groupByInitial(...), `renderItem` for
// each card, and an optional `columns` Tailwind class to override the
// default 3-col grid (used for the Bands view's 2-col layout).
function AlphaGrid({ groups, renderItem, columns = "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xxl }}>
      {groups.map(([letter, items]) => (
        <section key={letter} data-testid={`alpha-section-${letter}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
              marginBottom: tokens.space.md,
            }}
          >
            <span
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: tokens.color.orange,
                lineHeight: 1,
              }}
              aria-label={letter === "#" ? "Numbers and symbols" : `Letter ${letter}`}
            >
              {letter}
            </span>
            <div
              aria-hidden="true"
              style={{
                flex: 1,
                height: 1,
                background: tokens.color.divider,
              }}
            />
            <span
              aria-hidden="true"
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.16em",
                color: tokens.color.textMuted,
              }}
            >
              {items.length}
            </span>
          </div>
          <div className={`grid ${columns} gap-4`}>
            {items.map(renderItem)}
          </div>
        </section>
      ))}
    </div>
  );
}

export const Scene = () => {
  const [activeTab, setActiveTab] = useState("people");
  // Mobile drill-in: start on the list view; tap a tab to drill in,
  // back button returns to the list. Ignored on desktop (the Tabs
  // toolbar above is always visible there).
  const [mobileShowList, setMobileShowList] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showContactImport, setShowContactImport] = useState(false);
  const [phoneContacts, setPhoneContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [importingContacts, setImportingContacts] = useState(false);
  
  // Venue map state
  const [venueMapData, setVenueMapData] = useState([]);
  const [loadingMap, setLoadingMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Contact picker availability
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
  );
  const hasContactPicker = typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;
  const canImportContacts = isStandalone && hasContactPicker;
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleContactPickerImport = async () => {
    if (!canImportContacts) return;
    setLoadingContacts(true);
    try {
      const contacts = await navigator.contacts.select(
        ['name', 'tel'],
        { multiple: true }
      );
      if (contacts && contacts.length > 0) {
        setImportingContacts(true);
        let imported = 0;
        for (const contact of contacts) {
          const name = contact.name?.[0] || "Unknown";
          const phone = contact.tel?.[0] || "";
          try {
            await apiClient.post("/collaborators", {
              name,
              phone,
              instrument: "",
              notes: "Imported from phone contacts"
            });
            imported++;
          } catch (err) {
            // Skip duplicates silently
            if (err.response?.status !== 409) {
              console.error("Failed to import contact:", name, err);
            }
          }
        }
        toast.success(`Imported ${imported} contact${imported !== 1 ? 's' : ''}`);
        fetchCollaborators();
      }
    } catch (error) {
      if (error.name === 'SecurityError') {
        toast.error("Contact access denied. Check your browser permissions.");
      } else if (error.name !== 'AbortError') {
        toast.error("Failed to access contacts");
        console.error("Contact picker error:", error);
      }
    } finally {
      setLoadingContacts(false);
      setImportingContacts(false);
    }
  };
  
  // Collaborators state
  const [collaborators, setCollaborators] = useState([]);
  const [loadingCollabs, setLoadingCollabs] = useState(true);
  const [showCollabDialog, setShowCollabDialog] = useState(false);
  // Handle suggestions for the person being edited. Suggestions only; the
  // user opens each profile, picks one with "Use", then saves the form.
  const [findingHandles, setFindingHandles] = useState(false);
  const [handleResults, setHandleResults] = useState(null);
  const findCollabHandles = async () => {
    setFindingHandles(true);
    setHandleResults(null);
    try {
      const res = await apiClient.post("/collaborators/find-handles", {
        name: collabFormData.name,
        instrument: collabFormData.instrument,
      });
      setHandleResults(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't search for handles");
    } finally {
      setFindingHandles(false);
    }
  };
  const applyHandle = (candidate) => {
    const field = { instagram: "instagram_handle", x: "twitter_handle", facebook: "facebook_handle" }[candidate.platform];
    setCollabFormData(prev => ({ ...prev, [field]: candidate.handle }));
    toast.success("Handle added. Press Save to keep it.");
  };
  const [editingCollab, setEditingCollab] = useState(null);
  const [collabFormData, setCollabFormData] = useState({
    name: "",
    instrument: "",
    email: "",
    phone: "",
    instagram_handle: "",
    facebook_handle: "",
    twitter_handle: "",
    website: "",
    notes: ""
  });

  // Venues state
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [showVenueDialog, setShowVenueDialog] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [savingVenue, setSavingVenue] = useState(false);
  const [venueFormData, setVenueFormData] = useState({
    rating: null,
    parking_notes: "",
    food_recommendations: "",
    load_in_notes: "",
    green_room_notes: "",
    sound_notes: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    instagram_handle: "",
    facebook_handle: "",
    twitter_handle: "",
    freeagent_contact_id: "",
    xero_contact_id: "",
    address: "",
    website: ""
  });

  // Accounting integrations (for the optional contact-picker in venue/band dialogs)
  const [freeagentConnected, setFreeagentConnected] = useState(false);
  const [xeroConnected, setXeroConnected] = useState(false);
  const [accountingContacts, setAccountingContacts] = useState([]);  // merged FA + Xero only
  
  // Venue tips state
  const [showVenueTipsDialog, setShowVenueTipsDialog] = useState(false);
  const [selectedVenueForTips, setSelectedVenueForTips] = useState(null);
  const [venueTips, setVenueTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(false);
  const [newTip, setNewTip] = useState({ category: "general", content: "", google_maps_link: "" });
  const [addingTip, setAddingTip] = useState(false);
  
  // Bands state
  const [bands, setBands] = useState([]);
  const [loadingBands, setLoadingBands] = useState(true);
  
  // Suggested connections
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showBandDialog, setShowBandDialog] = useState(false);
  const [editingBand, setEditingBand] = useState(null);
  const [bandFormData, setBandFormData] = useState({
    name: "",
    instagram_handle: "",
    facebook_handle: "",
    twitter_handle: "",
    website: "",
    description: "",
    genre: "",
    contact_name: "",
    contact_email: "",
    freeagent_contact_id: "",
    xero_contact_id: ""
  });
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [selectedBandForMember, setSelectedBandForMember] = useState(null);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");

  // Timestamp of the last *forced* accounting-contact refresh (throttle).
  const lastContactRefreshRef = useRef(0);

  // Load the contacts that power the "Pull from accounting" picker in the
  // venue/band dialogs. With force=true we first ask the backend to bypass its
  // 1-hour FreeAgent cache, so a contact just added in FreeAgent shows up now.
  const loadAccountingContacts = async ({ force = false } = {}) => {
    try {
      if (force && freeagentConnected) {
        await apiClient.post("/accounting/freeagent/contacts/refresh").catch(() => {});
        lastContactRefreshRef.current = Date.now();
      }
      const r = await apiClient.get("/invoices/recipient-suggestions?recipient_type=all");
      // Only the external accounting entries — Fretless-sourced suggestions
      // are gigs/collaborators and aren't useful for setting a default.
      setAccountingContacts((r.data || []).filter(s => s.source === "freeagent" || s.source === "xero"));
    } catch (e) {
      // non-fatal; the picker just won't render / update
    }
  };

  useEffect(() => {
    fetchCollaborators();
    fetchVenues();
    fetchBands();
    fetchSuggestions();
    // Check accounting connections; fetch contacts only if at least one is wired up.
    // The contacts power the optional "Pull from accounting" picker in the venue/band dialogs.
    (async () => {
      try {
        const [faRes, xeroRes] = await Promise.all([
          apiClient.get("/accounting/freeagent/status").catch(() => ({ data: { connected: false } })),
          apiClient.get("/accounting/xero/status").catch(() => ({ data: { connected: false } })),
        ]);
        const fa = !!faRes.data?.connected;
        const xero = !!xeroRes.data?.connected;
        setFreeagentConnected(fa);
        setXeroConnected(xero);
        if (fa || xero) {
          // Pre-warm with the cached list; the dialog-open effect below force-
          // refreshes when the user actually opens a picker.
          await loadAccountingContacts();
        }
      } catch (e) {
        // non-fatal; the picker just won't render
      }
    })();
  }, []);

  // Auto-refresh accounting contacts whenever a picker-bearing dialog opens, so
  // a contact just added in FreeAgent/Xero appears without waiting out the
  // backend cache. Throttled to once a minute to avoid hammering the API.
  useEffect(() => {
    if (!showBandDialog && !showVenueDialog) return;
    if (!freeagentConnected && !xeroConnected) return;
    if (Date.now() - lastContactRefreshRef.current < 60_000) return;
    loadAccountingContacts({ force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBandDialog, showVenueDialog]);

  // ========== PHONE CONTACTS IMPORT ==========
  const importFromContacts = async () => {
    if (!isCapacitor) {
      toast.error("Contact import only works in the mobile app");
      return;
    }
    
    setLoadingContacts(true);
    setShowContactImport(true);
    
    try {
      // Dynamically import Capacitor Contacts plugin
      const { Contacts } = await import('@capacitor-community/contacts');
      
      // Request permission
      const permission = await Contacts.requestPermissions();
      if (permission.contacts !== 'granted') {
        toast.error("Permission to access contacts was denied");
        setLoadingContacts(false);
        return;
      }
      
      // Get contacts
      const result = await Contacts.getContacts({
        projection: {
          name: true,
          phones: true,
          emails: true,
          urls: true
        }
      });
      
      // Filter contacts that have at least a name
      const validContacts = result.contacts.filter(c => c.name?.display);
      
      // Extract social handles from URLs if available
      const processedContacts = validContacts.map(contact => {
        let instagram = '';
        let twitter = '';
        let facebook = '';
        
        // Check URLs for social profiles
        if (contact.urls) {
          for (const url of contact.urls) {
            const urlLower = (url.url || '').toLowerCase();
            if (urlLower.includes('instagram.com')) {
              const match = urlLower.match(/instagram\.com\/([^/?]+)/);
              if (match) instagram = match[1];
            } else if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
              const match = urlLower.match(/(?:twitter|x)\.com\/([^/?]+)/);
              if (match) twitter = match[1];
            } else if (urlLower.includes('facebook.com')) {
              const match = urlLower.match(/facebook\.com\/([^/?]+)/);
              if (match) facebook = match[1];
            }
          }
        }
        
        return {
          id: contact.contactId,
          name: contact.name?.display || '',
          email: contact.emails?.[0]?.address || '',
          phone: contact.phones?.[0]?.number || '',
          instagram_handle: instagram,
          twitter_handle: twitter,
          facebook_handle: facebook
        };
      });
      
      setPhoneContacts(processedContacts);
    } catch (error) {
      console.error("Error importing contacts:", error);
      toast.error("Failed to import contacts");
    } finally {
      setLoadingContacts(false);
    }
  };
  
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };
  
  const importSelectedContacts = async () => {
    if (selectedContacts.length === 0) {
      toast.error("Select at least one contact");
      return;
    }
    
    setImportingContacts(true);
    let imported = 0;
    
    try {
      for (const contactId of selectedContacts) {
        const contact = phoneContacts.find(c => c.id === contactId);
        if (contact) {
          await apiClient.post("/collaborators", {
            name: contact.name,
            email: contact.email,
            instagram_handle: contact.instagram_handle,
            twitter_handle: contact.twitter_handle,
            facebook_handle: contact.facebook_handle,
            notes: contact.phone ? `Phone: ${contact.phone}` : ''
          });
          imported++;
        }
      }
      
      toast.success(`Imported ${imported} contact(s)`);
      setShowContactImport(false);
      setSelectedContacts([]);
      setPhoneContacts([]);
      fetchCollaborators();
    } catch (error) {
      toast.error("Failed to import some contacts");
    } finally {
      setImportingContacts(false);
    }
  };

  // ========== COLLABORATORS ==========
  const fetchCollaborators = async () => {
    try {
      const response = await apiClient.get("/collaborators");
      setCollaborators(response.data);
    } catch (error) {
      toast.error("Failed to fetch collaborators");
    } finally {
      setLoadingCollabs(false);
    }
  };

  const resetCollabForm = () => {
    setCollabFormData({
      name: "",
      instrument: "",
      email: "",
      instagram_handle: "",
      facebook_handle: "",
      twitter_handle: "",
      website: "",
      notes: ""
    });
    setEditingCollab(null);
  };

  const handleOpenCollabDialog = (collab = null) => {
    if (collab) {
      setEditingCollab(collab);
      setCollabFormData({
        name: collab.name || "",
        instrument: collab.instrument || "",
        email: collab.email || "",
        instagram_handle: collab.instagram_handle || "",
        facebook_handle: collab.facebook_handle || "",
        twitter_handle: collab.twitter_handle || "",
        website: collab.website || "",
        notes: collab.notes || ""
      });
    } else {
      resetCollabForm();
    }
    setHandleResults(null);
    setShowCollabDialog(true);
  };

  const handleCollabSubmit = async (e) => {
    e.preventDefault();
    
    if (!collabFormData.name) {
      toast.error("Please enter a name");
      return;
    }

    try {
      if (editingCollab) {
        await apiClient.put(`/collaborators/${editingCollab.collaborator_id}`, collabFormData);
        toast.success("Contact updated");
      } else {
        await apiClient.post("/collaborators", collabFormData);
        toast.success("Contact added");
      }
      setShowCollabDialog(false);
      resetCollabForm();
      fetchCollaborators();
    } catch (error) {
      toast.error("Failed to save contact");
    }
  };

  const handleDeleteCollab = async (collabId) => {
    if (!confirm("Are you sure you want to delete this contact?")) return;
    
    try {
      await apiClient.delete(`/collaborators/${collabId}`);
      toast.success("Contact deleted");
      fetchCollaborators();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // ========== VENUES ==========
  const fetchVenues = async () => {
    try {
      const response = await apiClient.get("/venues");
      setVenues(response.data);
    } catch (error) {
      toast.error("Failed to fetch venues");
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleOpenVenueDialog = (venue) => {
    setEditingVenue(venue);
    setVenueFormData({
      rating: venue.rating || null,
      parking_notes: venue.parking_notes || "",
      food_recommendations: venue.food_recommendations || "",
      load_in_notes: venue.load_in_notes || "",
      green_room_notes: venue.green_room_notes || "",
      sound_notes: venue.sound_notes || "",
      contact_name: venue.contact_name || "",
      contact_email: venue.contact_email || "",
      contact_phone: venue.contact_phone || "",
      instagram_handle: venue.instagram_handle || "",
      facebook_handle: venue.facebook_handle || "",
      twitter_handle: venue.twitter_handle || "",
      freeagent_contact_id: venue.freeagent_contact_id || "",
      xero_contact_id: venue.xero_contact_id || "",
      address: venue.address || "",
      website: venue.website || ""
    });
    setShowVenueDialog(true);
  };

  const handleSaveVenue = async () => {
    if (!editingVenue) return;
    
    setSavingVenue(true);
    try {
      await apiClient.put(`/venues/${encodeURIComponent(editingVenue.name)}`, venueFormData);
      toast.success("Venue updated");
      setShowVenueDialog(false);
      fetchVenues();
    } catch (error) {
      toast.error("Failed to update venue");
    } finally {
      setSavingVenue(false);
    }
  };

  // ========== VENUE TIPS ==========
  const openVenueTips = async (venue) => {
    setSelectedVenueForTips(venue);
    setShowVenueTipsDialog(true);
    setLoadingTips(true);
    
    try {
      const response = await apiClient.get(`/venues/${venue.venue_id}/tips`);
      setVenueTips(response.data);
    } catch (error) {
      console.error("Failed to fetch tips:", error);
      setVenueTips([]);
    } finally {
      setLoadingTips(false);
    }
  };

  const addVenueTip = async () => {
    if (!selectedVenueForTips || !newTip.content.trim()) return;
    
    setAddingTip(true);
    try {
      await apiClient.post(`/venues/${selectedVenueForTips.venue_id}/tips`, newTip);
      toast.success("Tip added!");
      setNewTip({ category: "general", content: "", google_maps_link: "" });
      // Refresh tips
      const response = await apiClient.get(`/venues/${selectedVenueForTips.venue_id}/tips`);
      setVenueTips(response.data);
    } catch (error) {
      toast.error("Failed to add tip");
    } finally {
      setAddingTip(false);
    }
  };

  const reportTip = async (tipId) => {
    try {
      await apiClient.post(`/venues/tips/${tipId}/report`);
      toast.success("Tip reported. Thank you for helping keep the community safe.");
      // Refresh tips
      const response = await apiClient.get(`/venues/${selectedVenueForTips.venue_id}/tips`);
      setVenueTips(response.data);
    } catch (error) {
      toast.error("Failed to report tip");
    }
  };

  // ========== BANDS ==========
  const fetchBands = async () => {
    try {
      const response = await apiClient.get("/bands");
      setBands(response.data);
    } catch (error) {
      console.error("Failed to fetch bands:", error);
    } finally {
      setLoadingBands(false);
    }
  };

  // ========== SUGGESTED CONNECTIONS ==========
  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await apiClient.get("/collaborators/suggestions");
      setSuggestions(res.data.suggestions || []);
    } catch {
      // Silent fail
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAddSuggestion = async (sug) => {
    try {
      await apiClient.post("/collaborators/suggestions/add", { user_id: sug.user_id });
      toast.success(`Added ${sug.name} to your contacts!`);
      setSuggestions(prev => prev.filter(s => s.user_id !== sug.user_id));
      fetchCollaborators();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };


  // ========== VENUE MAP ==========
  const fetchVenueMapData = async () => {
    if (mapLoaded) return;
    setLoadingMap(true);
    try {
      const response = await apiClient.get("/maps/venues-geo");
      setVenueMapData(response.data.venues || []);
      setMapLoaded(true);
    } catch (error) {
      console.error("Failed to fetch venue map data:", error);
      toast.error("Failed to load venue map");
    } finally {
      setLoadingMap(false);
    }
  };

  // Load map data when switching to map tab
  useEffect(() => {
    if (activeTab === "map") {
      fetchVenueMapData();
    }
  }, [activeTab]);

  const handleBandSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBand) {
        await apiClient.put(`/bands/${editingBand.band_id}`, bandFormData);
        toast.success("Band updated!");
      } else {
        await apiClient.post("/bands", bandFormData);
        toast.success("Band created!");
      }
      setShowBandDialog(false);
      setEditingBand(null);
      setBandFormData({ name: "", instagram_handle: "", facebook_handle: "", twitter_handle: "", website: "", description: "", genre: "", contact_name: "", contact_email: "", freeagent_contact_id: "", xero_contact_id: "" });
      fetchBands();
    } catch (error) {
      toast.error("Failed to save band");
    }
  };

  const handleDeleteBand = async (bandId) => {
    if (!confirm("Delete this band? Members will not be deleted.")) return;
    
    try {
      await apiClient.delete(`/bands/${bandId}`);
      toast.success("Band deleted");
      fetchBands();
    } catch (error) {
      toast.error("Failed to delete band");
    }
  };

  const openEditBand = (band) => {
    setEditingBand(band);
    setBandFormData({
      name: band.name,
      instagram_handle: band.instagram_handle || "",
      facebook_handle: band.facebook_handle || "",
      twitter_handle: band.twitter_handle || "",
      website: band.website || "",
      description: band.description || "",
      genre: band.genre || "",
      contact_name: band.contact_name || "",
      contact_email: band.contact_email || "",
      freeagent_contact_id: band.freeagent_contact_id || "",
      xero_contact_id: band.xero_contact_id || ""
    });
    setShowBandDialog(true);
  };

  const openAddMember = (band) => {
    setSelectedBandForMember(band);
    setMemberSearchQuery("");
    setShowAddMemberDialog(true);
  };

  const addMemberToBand = async (collaboratorId) => {
    try {
      await apiClient.post(`/bands/${selectedBandForMember.band_id}/members`, { collaborator_id: collaboratorId });
      toast.success("Member added to band!");
      fetchBands();
    } catch (error) {
      toast.error("Failed to add member");
    }
  };

  const addNewMemberToBand = async (memberData) => {
    try {
      await apiClient.post(`/bands/${selectedBandForMember.band_id}/members`, memberData);
      toast.success("New member added!");
      fetchBands();
      fetchCollaborators(); // Refresh collaborators since we created a new one
      setShowAddMemberDialog(false);
    } catch (error) {
      toast.error("Failed to add member");
    }
  };

  const removeMemberFromBand = async (bandId, collaboratorId) => {
    try {
      await apiClient.delete(`/bands/${bandId}/members/${collaboratorId}`);
      toast.success("Member removed from band");
      fetchBands();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  // ========== FILTERING ==========
  const filteredCollaborators = collaborators.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.instrument?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredVenues = venues.filter(v => 
    v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.city?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredBands = bands.filter(b =>
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.genre?.toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => a.name.localeCompare(b.name));

  // For adding members to a band - filter available collaborators
  const availableCollaborators = memberSearchQuery 
    ? collaborators.filter(c => 
        c.name.toLowerCase().includes(memberSearchQuery.toLowerCase()) &&
        !selectedBandForMember?.member_ids?.includes(c.collaborator_id)
      )
    : [];

  // ========== RENDER HELPERS ==========
  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <button
            key={i}
            type="button"
            onClick={() => interactive && onSelect && onSelect(i)}
            className={interactive ? "p-1 hover:scale-110 transition-transform" : ""}
            disabled={!interactive}
          >
            <Star 
              className={`h-4 w-4 ${i <= (rating || 0) ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const CollaboratorCard = ({ collab }) => {
    const tk = tokens;
    return (
      <div
        style={{
          background: tk.color.cardBg,
          border: tk.border.card,
          borderRadius: tk.radius.xl,
          padding: tk.space.lg,
          transition: `border-color ${tk.motion.base}`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = tk.color.orangeLine)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = tk.color.cardBorder)}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: tk.space.md, gap: tk.space.sm }}>
          <div style={{ display: "flex", alignItems: "center", gap: tk.space.md, minWidth: 0, flex: 1 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tk.radius.pill,
                background: tk.color.innerBg,
                border: tk.border.inner,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <User style={{ width: 18, height: 18, color: tk.color.orange }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3
                style={{
                  margin: 0,
                  ...tk.type.titleSm,
                  color: tk.color.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {collab.name}
              </h3>
              {collab.instrument && (
                <p
                  style={{
                    margin: 0,
                    marginTop: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    ...tk.type.bodySm,
                    color: tk.color.textDim,
                  }}
                >
                  <Music2 style={{ width: 12, height: 12 }} />
                  {collab.instrument}
                </p>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => handleOpenCollabDialog(collab)}
              data-testid={`edit-collab-${collab.collaborator_id}`}
              title="Edit"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: tk.color.textMuted,
                borderRadius: tk.radius.md,
                cursor: "pointer",
                transition: `color ${tk.motion.fast}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.text)}
              onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
            >
              <Edit2 style={{ width: 14, height: 14 }} />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteCollab(collab.collaborator_id)}
              data-testid={`delete-collab-${collab.collaborator_id}`}
              title="Delete"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: tk.color.textMuted,
                borderRadius: tk.radius.md,
                cursor: "pointer",
                transition: `color ${tk.motion.fast}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.red)}
              onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
            >
              <Trash2 style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>

        {collab.notes && (
          <p
            className="line-clamp-2"
            style={{
              margin: 0,
              marginBottom: tk.space.md,
              ...tk.type.bodySm,
              color: tk.color.textDim,
            }}
          >
            {collab.notes}
          </p>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: tk.space.xs }}>
          {collab.email && (
            <ContactBadge href={`mailto:${collab.email}`} icon={Mail} label={collab.email} />
          )}
          {collab.instagram_handle && (
            <ContactBadge
              href={`https://instagram.com/${collab.instagram_handle}`}
              icon={Instagram}
              label={`@${collab.instagram_handle}`}
              external
            />
          )}
          {collab.twitter_handle && (
            <ContactBadge
              href={`https://x.com/${collab.twitter_handle}`}
              icon={XLogo}
              label={`@${collab.twitter_handle}`}
              external
            />
          )}
          {collab.website && (
            <ContactBadge
              href={collab.website.startsWith('http') ? collab.website : `https://${collab.website}`}
              icon={Globe}
              label="Website"
              external
            />
          )}
        </div>
      </div>
    );
  };

  // Small token-driven chip for contact handles (email / Instagram / etc).
  const ContactBadge = ({ href, icon: Icon, label, external }) => (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 8px",
        background: tokens.color.innerBg,
        border: tokens.border.inner,
        borderRadius: tokens.radius.sm,
        ...tokens.type.bodySm,
        fontSize: 12,
        color: tokens.color.textDim,
        textDecoration: "none",
        transition: `color ${tokens.motion.fast}, border-color ${tokens.motion.fast}`,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = tokens.color.text;
        e.currentTarget.style.borderColor = tokens.color.orangeLine;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = tokens.color.textDim;
        e.currentTarget.style.borderColor = tokens.color.innerBorder;
      }}
    >
      <Icon style={{ width: 12, height: 12, flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
    </a>
  );

  const VenueCard = ({ venue }) => {
    const tk = tokens;
    return (
      <div
        style={{
          background: tk.color.cardBg,
          border: tk.border.card,
          borderRadius: tk.radius.xl,
          padding: tk.space.lg,
          transition: `border-color ${tk.motion.base}`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = tk.color.orangeLine)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = tk.color.cardBorder)}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: tk.space.md, gap: tk.space.sm }}>
          <div style={{ display: "flex", alignItems: "center", gap: tk.space.md, minWidth: 0, flex: 1 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tk.radius.md,
                background: tk.color.orangeSoft,
                border: tk.border.brandSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MapPin style={{ width: 18, height: 18, color: tk.color.orange }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3
                style={{
                  margin: 0,
                  ...tk.type.titleSm,
                  color: tk.color.text,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {venue.name}
              </h3>
              <p
                style={{
                  margin: 0,
                  marginTop: 2,
                  ...tk.type.bodySm,
                  color: tk.color.textDim,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {venue.city}{venue.country ? `, ${venue.country}` : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 2, flexShrink: 0, position: "relative" }}>
            <button
              type="button"
              onClick={() => openVenueTips(venue)}
              title="Community tips"
              data-testid={`venue-tips-${venue.venue_id}`}
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: tk.color.textMuted,
                borderRadius: tk.radius.md,
                cursor: "pointer",
                position: "relative",
                transition: `color ${tk.motion.fast}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.text)}
              onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
            >
              <MessageSquare style={{ width: 14, height: 14 }} />
              {venue.tip_count > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: -2,
                    right: -2,
                    height: 16,
                    width: 16,
                    background: tk.color.orange,
                    color: tokens.color.onOrange,
                    fontSize: 10,
                    fontWeight: 600,
                    borderRadius: tk.radius.pill,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {venue.tip_count}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => handleOpenVenueDialog(venue)}
              data-testid={`edit-venue-${venue.name}`}
              title="Edit"
              style={{
                width: 32,
                height: 32,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "none",
                color: tk.color.textMuted,
                borderRadius: tk.radius.md,
                cursor: "pointer",
                transition: `color ${tk.motion.fast}`,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = tk.color.text)}
              onMouseLeave={(e) => (e.currentTarget.style.color = tk.color.textMuted)}
            >
              <Edit2 style={{ width: 14, height: 14 }} />
            </button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tk.space.lg,
            marginBottom: tk.space.md,
            ...tk.type.bodySm,
            color: tk.color.textDim,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Calendar style={{ width: 14, height: 14 }} />
            {venue.gig_count} gig{venue.gig_count !== 1 ? 's' : ''}
          </span>
          {venue.last_played && (
            <span>Last: {format(parseISO(venue.last_played), 'MMM yyyy')}</span>
          )}
          {venue.tip_count > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: tk.color.orange }}>
              <MessageSquare style={{ width: 12, height: 12 }} />
              {venue.tip_count} tip{venue.tip_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {venue.google_maps_link && (
          <a
            href={venue.google_maps_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleExternalNav}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              ...tk.type.bodySm,
              fontSize: 12,
              color: tk.color.orange,
              textDecoration: "none",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            <ExternalLink style={{ width: 12, height: 12 }} /> View on Maps
          </a>
        )}

        {(venue.instagram_handle || venue.twitter_handle || venue.facebook_handle) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: tk.space.xs, marginTop: tk.space.md }}>
            {venue.instagram_handle && (
              <ContactBadge
                href={`https://instagram.com/${venue.instagram_handle.replace(/^@/, "")}`}
                icon={Instagram}
                label={`@${venue.instagram_handle.replace(/^@/, "")}`}
                external
              />
            )}
            {venue.twitter_handle && (
              <ContactBadge
                href={`https://x.com/${venue.twitter_handle.replace(/^@/, "")}`}
                icon={XLogo}
                label={`@${venue.twitter_handle.replace(/^@/, "")}`}
                external
              />
            )}
            {venue.facebook_handle && (
              <ContactBadge
                href={venue.facebook_handle.startsWith("http") ? venue.facebook_handle : `https://facebook.com/${venue.facebook_handle}`}
                icon={Facebook}
                label="Facebook"
                external
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const loading = loadingCollabs || loadingVenues || loadingBands;

  return (
    <Layout>
      <div data-testid="scene-page">
        {/* Header — kicker own row, then h1 + Add Contact level (Gigs pattern) */}
        <div style={{ marginBottom: tokens.space.xxl }}>
          <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
            Your Network
          </Kicker>
          <div
            className="flex flex-row items-center justify-between"
            style={{ gap: tokens.space.md }}
          >
            <h1
              style={{
                margin: 0,
                ...tokens.type.heroSm,
                color: tokens.color.text,
                textWrap: "balance",
                minWidth: 0,
              }}
            >
              Scene
            </h1>
            <div style={{ display: "flex", gap: tokens.space.sm, flexShrink: 0, alignItems: "center" }}>
              {(canImportContacts || isCapacitor) && (
                <Button
                  variant="outline"
                  onClick={canImportContacts ? handleContactPickerImport : importFromContacts}
                  disabled={loadingContacts || importingContacts}
                  data-testid="import-contacts-btn"
                >
                  {loadingContacts || importingContacts ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Smartphone className="h-4 w-4 mr-2" />
                  )}
                  <span className="hidden sm:inline">Import from Phone</span>
                </Button>
              )}
              <button
                type="button"
                onClick={() => handleOpenCollabDialog()}
                data-testid="add-contact-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "10px 16px",
                  background: tokens.color.orange,
                  color: tokens.color.onOrange,
                  border: tokens.border.brand,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                <Plus className="h-4 w-4" />
                Add Contact
              </button>
            </div>
          </div>
        </div>

        {/* Toolbar — Gigs-style: tabs and search on a single row, content
            below at full width. Replaces the earlier sidebar+content
            arrangement so the page lays out the same way as /gigs. */}
        {(() => {
          const sceneNavItems = [
            { value: "people", icon: Users, label: `People (${collaborators.length})` },
            { value: "bands", icon: Music2, label: `Bands (${bands.length})` },
            { value: "venues", icon: Building2, label: `Venues (${venues.length})` },
            { value: "map", icon: MapIcon, label: "Map" },
          ];
          if (suggestions.length > 0) sceneNavItems.push({ value: "suggestions", icon: Star, label: `Suggestions (${suggestions.length})` });

          return (
            <Tabs
              value={activeTab}
              onValueChange={(v) => { setActiveTab(v); setMobileShowList(false); }}
              className="w-full"
            >
              {/* Desktop toolbar — tabs + search on one row */}
              <div
                className="hidden md:flex flex-wrap items-center"
                style={{ gap: tokens.space.md, marginBottom: tokens.space.xl }}
              >
                <TabsList
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${sceneNavItems.length}, minmax(0, auto))`,
                    flexShrink: 0,
                  }}
                >
                  {sceneNavItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <TabsTrigger
                        key={item.value}
                        value={item.value}
                        data-testid={`${item.value}-tab`}
                        className="flex items-center gap-1.5"
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <div className="relative" style={{ flex: "1 1 200px", minWidth: 160 }}>
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10"
                    style={{ color: tokens.color.textMuted }}
                  />
                  <Input
                    placeholder="Search people or venues..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-scene"
                  />
                </div>
              </div>

              {/* Mobile drill-in — list of nav items, or back+search when
                  drilled into a specific tab. Matches the Settings page
                  pattern. */}
              <div className="md:hidden" style={{ marginBottom: tokens.space.lg }}>
                {mobileShowList ? (
                  <div data-testid="scene-mobile-list" style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                    {sceneNavItems.map(item => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.value}
                          onClick={() => { setActiveTab(item.value); setMobileShowList(false); }}
                          data-testid={`${item.value}-tab-mobile`}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: tokens.space.lg,
                            padding: "14px 16px",
                            minHeight: 52,
                            borderRadius: tokens.radius.md,
                            background: tokens.color.innerBg,
                            border: tokens.border.inner,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <Icon style={{ width: 18, height: 18, color: tokens.color.textMuted, flexShrink: 0 }} />
                          <span style={{ flex: 1, ...tokens.type.bodySm, fontWeight: 500, color: tokens.color.text }}>
                            {item.label}
                          </span>
                          <ChevronRight style={{ width: 16, height: 16, color: tokens.color.textMuted }} />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
                    <button
                      type="button"
                      onClick={() => setMobileShowList(true)}
                      data-testid="scene-mobile-back"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: tokens.space.sm,
                        color: tokens.color.orange,
                        ...tokens.type.bodySm,
                        fontWeight: 500,
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        alignSelf: "flex-start",
                      }}
                    >
                      <ArrowLeft style={{ width: 16, height: 16 }} /> Scene
                    </button>
                    <div className="relative">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10"
                        style={{ color: tokens.color.textMuted }}
                      />
                      <Input
                        placeholder="Search people or venues..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-scene-mobile"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Content panel — desktop always, mobile only when drilled in */}
              <div className={`min-w-0 ${mobileShowList ? "hidden md:block" : ""}`}>

          {/* People Tab */}
          {activeTab === "people" && (
            <>
            {/* Suggested Connections */}
            {suggestions.length > 0 && (
              <Card className="mb-4 border-amber-500/20 bg-amber-500/5" data-testid="gig-suggestions">
                <CardContent className="p-4">
                  <h3 className="text-sm font-medium text-amber-200 mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" /> People you've gigged with
                  </h3>
                  <div className="space-y-2">
                    {suggestions.slice(0, 5).map(sug => (
                      <div key={sug.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30">
                        {sug.picture ? (
                          <img src={sug.picture} alt={sug.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{sug.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">
                            Played at {sug.venue_name} on {sug.date}{sug.city ? `, ${sug.city}` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-primary text-xs shrink-0"
                          onClick={() => handleAddSuggestion(sug)}
                          data-testid={`add-suggestion-${sug.user_id}`}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Contact import buttons */}
            {(canImportContacts || (isStandalone && isIOS)) && (
              <div className="mb-4">
                {canImportContacts ? (
                  <Button
                    onClick={handleContactPickerImport}
                    disabled={loadingContacts || importingContacts}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="import-contacts-btn"
                  >
                    {loadingContacts || importingContacts ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{importingContacts ? "Importing..." : "Opening contacts..."}</>
                    ) : (
                      <><Import className="h-4 w-4 mr-2" />Import from contacts</>
                    )}
                  </Button>
                ) : isStandalone && isIOS ? (
                  <Card className="bg-card border-border">
                    <CardContent className="py-3 px-4 flex items-center gap-3">
                      <Smartphone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        Contact import is available on Android. On iOS, add contacts manually.
                      </p>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            )}
            {loadingCollabs ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-40 bg-card border border-border rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredCollaborators.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? "No contacts match your search" : "No contacts yet"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={() => handleOpenCollabDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Contact
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <AlphaGrid
                groups={groupByInitial(filteredCollaborators, (c) => c.name)}
                renderItem={(collab) => (
                  <CollaboratorCard key={collab.collaborator_id} collab={collab} />
                )}
              />
            )}
            </>
          )}

          {/* Venues Tab */}
          {activeTab === "venues" && (
            <>
            {loadingVenues ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-40 bg-card border border-border rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredVenues.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery ? "No venues match your search" : "No venues yet. Venues appear here as you add gigs."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <AlphaGrid
                groups={groupByInitial(filteredVenues, (v) => v.name)}
                renderItem={(venue) => (
                  <VenueCard key={venue.venue_id || venue.name} venue={venue} />
                )}
              />
            )}
            </>
          )}

          {/* Bands Tab */}
          {activeTab === "bands" && (
            <>
            <div className="flex justify-end mb-4">
              <Button onClick={() => { setEditingBand(null); setBandFormData({ name: "", instagram_handle: "", facebook_handle: "", twitter_handle: "", website: "", description: "", genre: "" }); setShowBandDialog(true); }} data-testid="add-band-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Band
              </Button>
            </div>
            
            {loadingBands ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-48 bg-card border border-border rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredBands.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Music2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery ? "No bands match your search" : "No bands yet. Create a band to group musicians together."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <AlphaGrid
                columns="grid-cols-1 md:grid-cols-2"
                groups={groupByInitial(filteredBands, (b) => b.name)}
                renderItem={(band) => (
                  <Card key={band.band_id} className="bg-card border-border hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{band.name}</CardTitle>
                          {band.genre && <Badge variant="secondary" className="mt-1">{band.genre}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditBand(band)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteBand(band.band_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {band.description && <p className="text-sm text-muted-foreground">{band.description}</p>}
                      
                      {/* Social handles */}
                      <div className="flex gap-2">
                        {band.instagram_handle && (
                          <a href={`https://instagram.com/${band.instagram_handle}`} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav}>
                            <Badge variant="outline" className="gap-1">
                              <Instagram className="h-3 w-3" /> @{band.instagram_handle}
                            </Badge>
                          </a>
                        )}
                        {band.twitter_handle && (
                          <a href={`https://x.com/${band.twitter_handle}`} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav}>
                            <Badge variant="outline" className="gap-1">
                              <XLogo className="h-3 w-3" /> @{band.twitter_handle}
                            </Badge>
                          </a>
                        )}
                      </div>
                      
                      {/* Members */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Members ({band.members?.length || 0})</span>
                          <Button variant="ghost" size="sm" onClick={() => openAddMember(band)} data-testid={`add-member-${band.band_id}`}>
                            <Plus className="h-3 w-3 mr-1" /> Add
                          </Button>
                        </div>
                        {band.members && band.members.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {band.members.map(member => (
                              <Badge key={member.collaborator_id} variant="secondary" className="gap-1 pr-1">
                                {member.name} {member.instrument && `(${member.instrument})`}
                                <button 
                                  onClick={() => removeMemberFromBand(band.band_id, member.collaborator_id)}
                                  className="ml-1 hover:text-destructive"
                                >
                                  ×
                                </button>
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">No members yet. Add existing people or create new ones.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              />
            )}
            </>
          )}

          {/* Map Tab */}
          {activeTab === "map" && (
            <>
            {loadingMap ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-muted-foreground">Loading venue map...</p>
                </CardContent>
              </Card>
            ) : venueMapData.filter(v => v.lat && v.lng).length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <MapIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg mb-2">No venues to show</h3>
                  <p className="text-muted-foreground">
                    {venueMapData.length > 0
                      ? "Your venues couldn't be geocoded. Try adding addresses to your gigs."
                      : "Add gigs with venue names to see them on the map."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <Card className="bg-card border-border overflow-hidden">
                  <CardContent className="p-0">
                    <div style={{ height: "500px", width: "100%" }} data-testid="venue-map-container">
                      <MapContainer
                        center={(() => {
                          const mapped = venueMapData.filter(v => v.lat && v.lng);
                          if (mapped.length === 0) return [51.5, -0.12];
                          const avgLat = mapped.reduce((s, v) => s + v.lat, 0) / mapped.length;
                          const avgLng = mapped.reduce((s, v) => s + v.lng, 0) / mapped.length;
                          return [avgLat, avgLng];
                        })()}
                        zoom={venueMapData.filter(v => v.lat && v.lng).length <= 1 ? 12 : 6}
                        style={{ height: "100%", width: "100%" }}
                        scrollWheelZoom={true}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {venueMapData
                          .filter(v => v.lat && v.lng)
                          .map((venue, idx) => (
                            <Marker key={idx} position={[venue.lat, venue.lng]} icon={venueIcon}>
                              <Popup>
                                <div className="text-sm">
                                  <p className="font-semibold">{venue.name}</p>
                                  <p className="text-muted-foreground">{venue.city}{venue.country ? `, ${venue.country}` : ""}</p>
                                  <p className="text-xs mt-1">{venue.gig_count} gig{venue.gig_count !== 1 ? "s" : ""}</p>
                                  {venue.last_played && (
                                    <p className="text-xs">Last: {format(parseISO(venue.last_played), "MMM yyyy")}</p>
                                  )}
                                  <a
                                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${venue.name}, ${venue.city}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                      if (isStandalone) {
                                        e.preventDefault();
                                        window.location.href = e.currentTarget.href;
                                      }
                                    }}
                                    className="text-xs text-blue-500 hover:underline mt-1 block"
                                  >
                                    Get Directions
                                  </a>
                                </div>
                              </Popup>
                            </Marker>
                          ))}
                      </MapContainer>
                    </div>
                  </CardContent>
                </Card>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-primary">{venueMapData.length}</p>
                      <p className="text-xs text-muted-foreground">Total Venues</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-primary">
                        {venueMapData.filter(v => v.lat && v.lng).length}
                      </p>
                      <p className="text-xs text-muted-foreground">On Map</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-primary">
                        {new Set(venueMapData.map(v => v.city).filter(Boolean)).size}
                      </p>
                      <p className="text-xs text-muted-foreground">Cities</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-border">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-primary">
                        {venueMapData.reduce((s, v) => s + (v.gig_count || 0), 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">Total Gigs</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
            </>
          )}
          {activeTab === "suggestions" && (
            <>
            {loadingSuggestions ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-muted-foreground">Loading suggestions...</p>
                </CardContent>
              </Card>
            ) : suggestions.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg mb-2">No suggestions</h3>
                  <p className="text-muted-foreground">
                    People you've gigged with will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {suggestions.map(sug => (
                  <Card key={sug.user_id} className="bg-card border-border hover:border-primary/30 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {sug.picture ? (
                          <img src={sug.picture} alt={sug.name} className="w-10 h-10 rounded-full flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{sug.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Played at {sug.venue_name} on {sug.date}{sug.city ? `, ${sug.city}` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddSuggestion(sug)}
                          data-testid={`add-suggestion-${sug.user_id}`}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            </>
          )}
              </div>
            </Tabs>
          );
        })()}

        {/* Collaborator Dialog */}
        <Dialog open={showCollabDialog} onOpenChange={setShowCollabDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCollab ? "Edit Contact" : "Add Contact"}</DialogTitle>
              <DialogDescription>
                Add band members, promoters, booking agents, or other contacts
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCollabSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={collabFormData.name}
                    onChange={(e) => setCollabFormData({ ...collabFormData, name: e.target.value })}
                    required
                    data-testid="collab-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instrument">Instrument / Role</Label>
                  <Input
                    id="instrument"
                    value={collabFormData.instrument}
                    onChange={(e) => setCollabFormData({ ...collabFormData, instrument: e.target.value })}
                    data-testid="collab-instrument"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={collabFormData.email}
                  onChange={(e) => setCollabFormData({ ...collabFormData, email: e.target.value })}
                  data-testid="collab-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Instagram className="h-3 w-3" /> Instagram
                  </Label>
                  <Input
                    value={collabFormData.instagram_handle}
                    onChange={(e) => setCollabFormData({ ...collabFormData, instagram_handle: e.target.value })}
                    data-testid="collab-instagram"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <XLogo className="h-3 w-3" /> X
                  </Label>
                  <Input
                    value={collabFormData.twitter_handle}
                    onChange={(e) => setCollabFormData({ ...collabFormData, twitter_handle: e.target.value })}
                    data-testid="collab-x"
                  />
                </div>
              </div>

              <div className="space-y-2" data-testid="collab-find-handles">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={findCollabHandles}
                  disabled={!collabFormData.name?.trim() || findingHandles}
                  data-testid="collab-find-handles-btn"
                >
                  {findingHandles ? "Searching…" : "Find handles"}
                </Button>
                {!collabFormData.name?.trim() && (
                  <p className="text-xs text-muted-foreground">Add a name first. Adding an instrument helps narrow the results.</p>
                )}
                {handleResults && (
                  <div className="space-y-2 rounded-md border border-border p-2">
                    {handleResults.candidates.length === 0 && (
                      <p className="text-xs text-muted-foreground">No profiles found automatically. Check by hand with the links below.</p>
                    )}
                    {handleResults.candidates.map(c => (
                      <div key={`${c.platform}:${c.handle}`} className="flex items-start gap-2 text-xs" data-testid="handle-candidate">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{{ instagram: "Instagram", x: "X", facebook: "Facebook" }[c.platform]}: {c.platform === "facebook" ? c.handle : `@${c.handle}`}</p>
                          {c.title && <p className="text-muted-foreground truncate">{c.title}</p>}
                        </div>
                        <a href={c.url} target="_blank" rel="noopener noreferrer" className="underline shrink-0">Open profile</a>
                        <Button type="button" size="sm" variant="secondary" onClick={() => applyHandle(c)}>Use</Button>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-3 text-xs pt-1">
                      {handleResults.search_links.map(l => (
                        <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="underline text-muted-foreground">
                          {l.label} ↗
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Facebook className="h-3 w-3" /> Facebook
                  </Label>
                  <Input
                    value={collabFormData.facebook_handle}
                    onChange={(e) => setCollabFormData({ ...collabFormData, facebook_handle: e.target.value })}
                    data-testid="collab-facebook"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Phone
                  </Label>
                  <Input
                    value={collabFormData.phone || ""}
                    onChange={(e) => setCollabFormData({ ...collabFormData, phone: e.target.value })}
                    data-testid="collab-phone"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Globe className="h-3 w-3" /> Website
                </Label>
                <Input
                  value={collabFormData.website}
                  onChange={(e) => setCollabFormData({ ...collabFormData, website: e.target.value })}
                  placeholder="https://..."
                  data-testid="collab-website"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={collabFormData.notes}
                  onChange={(e) => setCollabFormData({ ...collabFormData, notes: e.target.value })}
                  rows={3}
                  data-testid="collab-notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCollabDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-collab">
                  {editingCollab ? "Save Changes" : "Add Contact"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Venue Dialog */}
        <Dialog open={showVenueDialog} onOpenChange={setShowVenueDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Venue Details</DialogTitle>
              <DialogDescription>
                {editingVenue?.name} - {editingVenue?.city}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Rating */}
              <div className="space-y-2">
                <Label>Rating</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setVenueFormData({ ...venueFormData, rating: venueFormData.rating === i ? null : i })}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star 
                        className={`h-6 w-6 ${i <= (venueFormData.rating || 0) ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={venueFormData.address}
                    onChange={(e) => setVenueFormData({ ...venueFormData, address: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={venueFormData.website}
                    onChange={(e) => setVenueFormData({ ...venueFormData, website: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Car className="h-4 w-4" /> Parking Notes
                </Label>
                <Textarea
                  value={venueFormData.parking_notes}
                  onChange={(e) => setVenueFormData({ ...venueFormData, parking_notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Utensils className="h-4 w-4" /> Food & Drink
                </Label>
                <Textarea
                  value={venueFormData.food_recommendations}
                  onChange={(e) => setVenueFormData({ ...venueFormData, food_recommendations: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Music2 className="h-4 w-4" /> Sound / Tech Notes
                </Label>
                <Textarea
                  value={venueFormData.sound_notes}
                  onChange={(e) => setVenueFormData({ ...venueFormData, sound_notes: e.target.value })}
                  rows={2}
                />
              </div>

              {(freeagentConnected || xeroConnected) && accountingContacts.length > 0 && (
                <div className="space-y-2">
                  <Label>Pull contact from accounting</Label>
                  <SearchableSelect
                    items={accountingContacts.map((c, idx) => ({
                      value: `${c.source}__${idx}`,
                      label: c.name,
                      sublabel: c.email || c.address || undefined,
                      keywords: [c.email, c.address].filter(Boolean),
                      badge: c.source === "freeagent"
                        ? { label: "FA", color: "bg-emerald-500/10 text-emerald-400" }
                        : { label: "XERO", color: "bg-sky-500/10 text-sky-400" },
                      group: c.source === "freeagent" ? "FreeAgent" : "Xero",
                    }))}
                    groupOrder={["FreeAgent", "Xero"]}
                    value=""
                    onChange={(val) => {
                      const idx = parseInt(val.split("__").pop(), 10);
                      const picked = accountingContacts[idx];
                      if (!picked) return;
                      setVenueFormData(prev => ({
                        ...prev,
                        contact_name: picked.name || prev.contact_name,
                        contact_email: picked.email || prev.contact_email,
                        freeagent_contact_id: picked.source === "freeagent" ? (picked.freeagent_url || "") : prev.freeagent_contact_id,
                        xero_contact_id: picked.source === "xero" ? (picked.xero_contact_id || "") : prev.xero_contact_id,
                      }));
                    }}
                    placeholder="Search FreeAgent / Xero contacts..."
                    searchPlaceholder="Type to search..."
                    emptyMessage="No matching contacts"
                  />
                  <p className="text-xs text-muted-foreground">
                    Picks fill the name and email below — edit them if needed.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={venueFormData.contact_name}
                    onChange={(e) => setVenueFormData({ ...venueFormData, contact_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={venueFormData.contact_email}
                    onChange={(e) => setVenueFormData({ ...venueFormData, contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={venueFormData.contact_phone}
                    onChange={(e) => setVenueFormData({ ...venueFormData, contact_phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Instagram className="h-3 w-3" /> Instagram
                  </Label>
                  <Input
                    value={venueFormData.instagram_handle}
                    onChange={(e) => setVenueFormData({ ...venueFormData, instagram_handle: e.target.value })}
                    data-testid="venue-instagram"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <XLogo className="h-3 w-3" /> X
                  </Label>
                  <Input
                    value={venueFormData.twitter_handle}
                    onChange={(e) => setVenueFormData({ ...venueFormData, twitter_handle: e.target.value })}
                    data-testid="venue-x"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Facebook className="h-3 w-3" /> Facebook
                  </Label>
                  <Input
                    value={venueFormData.facebook_handle}
                    onChange={(e) => setVenueFormData({ ...venueFormData, facebook_handle: e.target.value })}
                    data-testid="venue-facebook"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVenueDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveVenue}
                disabled={savingVenue}
                className="bg-primary text-primary-foreground"
                data-testid="save-venue"
              >
                {savingVenue ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contact Import Dialog */}
        <Dialog open={showContactImport} onOpenChange={setShowContactImport}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Import from Contacts</DialogTitle>
              <DialogDescription>
                Select contacts to add as collaborators. Social handles will be imported if available.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto py-4">
              {loadingContacts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : !isCapacitor ? (
                <div className="text-center py-8">
                  <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Contact import is only available in the mobile app.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Download Fretless on iOS or Android to import contacts.
                  </p>
                </div>
              ) : phoneContacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No contacts found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-muted-foreground">
                      {phoneContacts.length} contacts found
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (selectedContacts.length === phoneContacts.length) {
                          setSelectedContacts([]);
                        } else {
                          setSelectedContacts(phoneContacts.map(c => c.id));
                        }
                      }}
                    >
                      {selectedContacts.length === phoneContacts.length ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                  
                  {phoneContacts.map(contact => (
                    <div
                      key={contact.id}
                      onClick={() => toggleContactSelection(contact.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedContacts.includes(contact.id)
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{contact.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {contact.email && <span>{contact.email}</span>}
                            {contact.instagram_handle && (
                              <span className="flex items-center gap-1">
                                <Instagram className="h-3 w-3" />
                                @{contact.instagram_handle}
                              </span>
                            )}
                            {contact.twitter_handle && (
                              <span className="flex items-center gap-1">
                                <XLogo className="h-3 w-3" />
                                @{contact.twitter_handle}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedContacts.includes(contact.id) && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContactImport(false)}>
                Cancel
              </Button>
              <Button
                onClick={importSelectedContacts}
                disabled={selectedContacts.length === 0 || importingContacts}
              >
                {importingContacts ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Import className="h-4 w-4 mr-2" />
                    Import {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Band Dialog */}
        <Dialog open={showBandDialog} onOpenChange={setShowBandDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBand ? "Edit Band" : "Create Band"}</DialogTitle>
              <DialogDescription>
                A band groups musicians together. You can select which members perform at each gig.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleBandSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="band-name">Band Name *</Label>
                <Input
                  id="band-name"
                  value={bandFormData.name}
                  onChange={(e) => setBandFormData({ ...bandFormData, name: e.target.value })}
                  required
                  data-testid="band-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="band-genre">Genre</Label>
                  <Input
                    id="band-genre"
                    value={bandFormData.genre}
                    onChange={(e) => setBandFormData({ ...bandFormData, genre: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="band-website">Website</Label>
                  <Input
                    id="band-website"
                    value={bandFormData.website}
                    onChange={(e) => setBandFormData({ ...bandFormData, website: e.target.value })}
                    placeholder="https://"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="band-desc">Description</Label>
                <Textarea
                  id="band-desc"
                  value={bandFormData.description}
                  onChange={(e) => setBandFormData({ ...bandFormData, description: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Instagram className="h-3 w-3" /> Instagram</Label>
                  <Input
                    value={bandFormData.instagram_handle}
                    onChange={(e) => setBandFormData({ ...bandFormData, instagram_handle: e.target.value.replace('@', '') })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><XLogo className="h-3 w-3" /> X (Twitter)</Label>
                  <Input
                    value={bandFormData.twitter_handle}
                    onChange={(e) => setBandFormData({ ...bandFormData, twitter_handle: e.target.value.replace('@', '') })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Facebook className="h-3 w-3" /> Facebook</Label>
                  <Input
                    value={bandFormData.facebook_handle}
                    onChange={(e) => setBandFormData({ ...bandFormData, facebook_handle: e.target.value })}
                  />
                </div>
              </div>

              {/* Invoice contact (pre-fills invoice recipient on gigs linked to this band) */}
              <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
                <div>
                  <Label>Invoice Contact (optional)</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Pre-fills the invoice recipient when invoicing gigs linked to this band.
                  </p>
                </div>
                {(freeagentConnected || xeroConnected) && accountingContacts.length > 0 && (
                  <SearchableSelect
                    items={accountingContacts.map((c, idx) => ({
                      value: `${c.source}__${idx}`,
                      label: c.name,
                      sublabel: c.email || c.address || undefined,
                      keywords: [c.email, c.address].filter(Boolean),
                      badge: c.source === "freeagent"
                        ? { label: "FA", color: "bg-emerald-500/10 text-emerald-400" }
                        : { label: "XERO", color: "bg-sky-500/10 text-sky-400" },
                      group: c.source === "freeagent" ? "FreeAgent" : "Xero",
                    }))}
                    groupOrder={["FreeAgent", "Xero"]}
                    value=""
                    onChange={(val) => {
                      const idx = parseInt(val.split("__").pop(), 10);
                      const picked = accountingContacts[idx];
                      if (!picked) return;
                      setBandFormData(prev => ({
                        ...prev,
                        contact_name: picked.name || prev.contact_name,
                        contact_email: picked.email || prev.contact_email,
                        freeagent_contact_id: picked.source === "freeagent" ? (picked.freeagent_url || "") : prev.freeagent_contact_id,
                        xero_contact_id: picked.source === "xero" ? (picked.xero_contact_id || "") : prev.xero_contact_id,
                      }));
                    }}
                    placeholder="Search FreeAgent / Xero contacts..."
                    searchPlaceholder="Type to search..."
                    emptyMessage="No matching contacts"
                  />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="band-contact-name" className="text-xs">Contact name</Label>
                    <Input
                      id="band-contact-name"
                      value={bandFormData.contact_name}
                      onChange={(e) => setBandFormData({ ...bandFormData, contact_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="band-contact-email" className="text-xs">Contact email</Label>
                    <Input
                      id="band-contact-email"
                      type="email"
                      value={bandFormData.contact_email}
                      onChange={(e) => setBandFormData({ ...bandFormData, contact_email: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowBandDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-band">{editingBand ? "Save Changes" : "Create Band"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Member to Band Dialog */}
        <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Member to {selectedBandForMember?.name}</DialogTitle>
              <DialogDescription>
                Search existing people or create a new one. New members also appear in your People list.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Search Existing People</Label>
                <Input
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                  placeholder="Type to search..."
                  data-testid="member-search"
                />
                
                {memberSearchQuery && availableCollaborators.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {availableCollaborators.map(collab => (
                      <button
                        key={collab.collaborator_id}
                        type="button"
                        onClick={() => { addMemberToBand(collab.collaborator_id); setMemberSearchQuery(""); }}
                        className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center justify-between"
                      >
                        <span>{collab.name} {collab.instrument && <span className="text-muted-foreground">({collab.instrument})</span>}</span>
                        <Plus className="h-4 w-4" />
                      </button>
                    ))}
                  </div>
                )}
                
                {memberSearchQuery && availableCollaborators.length === 0 && (
                  <p className="text-sm text-muted-foreground">No matches found. Create a new person below.</p>
                )}
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Or create a new member:</p>
                <div className="space-y-3">
                  <Input
                    data-testid="new-member-name"
                    id="new-member-name"
                  />
                  <Input
                    data-testid="new-member-instrument"
                    id="new-member-instrument"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      data-testid="new-member-instagram"
                      id="new-member-instagram"
                    />
                    <Input
                      data-testid="new-member-twitter"
                      id="new-member-twitter"
                    />
                  </div>
                  <Button 
                    type="button"
                    className="w-full"
                    onClick={() => {
                      const name = document.getElementById('new-member-name').value;
                      if (!name.trim()) { toast.error("Name is required"); return; }
                      addNewMemberToBand({
                        name: name.trim(),
                        instrument: document.getElementById('new-member-instrument').value.trim() || null,
                        instagram_handle: document.getElementById('new-member-instagram').value.replace('@', '').trim() || null,
                        twitter_handle: document.getElementById('new-member-twitter').value.replace('@', '').trim() || null
                      });
                    }}
                    data-testid="create-member"
                  >
                    Create & Add to Band
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Venue Tips Dialog */}
        <Dialog open={showVenueTipsDialog} onOpenChange={setShowVenueTipsDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Tips for {selectedVenueForTips?.name}
              </DialogTitle>
              <DialogDescription>
                Community tips from musicians who've played here. Add your own to help others!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Existing Tips */}
              {loadingTips ? (
                <div className="text-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </div>
              ) : venueTips.length > 0 ? (
                <div className="space-y-3">
                  {venueTips.map(tip => (
                    <div key={tip.tip_id} className="p-3 bg-secondary/30 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <Badge variant="outline" className="mb-2 capitalize">{tip.category.replace('_', ' ')}</Badge>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => reportTip(tip.tip_id)} title="Report inappropriate tip">
                          <Flag className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                      <p className="text-sm">{tip.content}</p>
                      {tip.google_maps_link && (
                        <a href={tip.google_maps_link} target="_blank" rel="noopener noreferrer" onClick={handleExternalNav} className="text-xs text-primary flex items-center gap-1 mt-2">
                          <ExternalLink className="h-3 w-3" /> View on Google Maps
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No tips yet. Be the first to add one!</p>
              )}
              
              {/* Add New Tip */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-medium">Add a Tip</h4>
                <Select value={newTip.category} onValueChange={(v) => setNewTip({ ...newTip, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parking"><Car className="h-4 w-4 mr-2 inline" /> Parking</SelectItem>
                    <SelectItem value="food"><Utensils className="h-4 w-4 mr-2 inline" /> Food</SelectItem>
                    <SelectItem value="load_in">Load In</SelectItem>
                    <SelectItem value="green_room">Green Room</SelectItem>
                    <SelectItem value="sound">Sound</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  value={newTip.content}
                  onChange={(e) => setNewTip({ ...newTip, content: e.target.value })}
                  rows={2}
                />
                <Input
                  value={newTip.google_maps_link}
                  onChange={(e) => setNewTip({ ...newTip, google_maps_link: e.target.value })}
                />
                <Button onClick={addVenueTip} disabled={!newTip.content.trim() || addingTip} className="w-full">
                  {addingTip ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Add Tip
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Scene;
